import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HasSubscription } from 'src/ca-shared/ca-shared.module';
import { CustomPhraseTypeDto } from 'src/core/models/conversation/phrase-type/custom-phrase-type.dto';
import { LocalizationService } from '@abp/ng.core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Confirmation, ConfirmationService, ToasterService } from '@abp/ng.theme.shared';
import { StringValidator } from 'src/core/validators/shared/string.validator';
import { CallLanguageCodeDto } from 'src/core/models/call/call-language-code.dto';
import { PhraseTypeSettingsService } from 'src/core/services/settings/phrase-type-settings.service';
import { ConversationCustomPhraseTypeMode } from 'src/core/models/generic-lookup-type/conversation/custom-phrase-type-mode.glt';
import { takeUntil, map, take } from 'rxjs';
import { GenericLookupTypeState } from 'src/core/states/generic-lookup-type/generic-lookup-type.state';
import { GenericLookupDto } from 'src/core/models/generic-lookup/generic-lookup.dto';
import { Store } from '@ngxs/store';
import { CustomPhraseTypeValidator } from 'src/core/validators/conversation/custom-phrase-type-validator';
import { PhraseTypeValidator } from 'src/core/validators/conversation/phrase-type-validator';
import { PhraseTypeRegexDetailPatternValidator } from 'src/core/validators/conversation/phrase-type-regex-detail-pattern.validator';

@Component({
  selector: 'ca-custom-phrase-type',
  templateUrl: './custom-phrase-type.component.html',
  styleUrls: ['./custom-phrase-type.component.scss'],
  standalone: false,
})
export class CustomPhraseTypeComponent extends HasSubscription implements OnInit {
  @ViewChild('customPhraseTypeModal') customPhraseTypeModal: any;

  processing = false;
  customForm: FormGroup;
  customPhraseTypeList: CustomPhraseTypeDto[] = [];
  initiallySelectedCustomPhraseTypeIds: number[] = [];
  selectedCustomPhraseTypeIds: number[] = [];
  languagesDataSource: CallLanguageCodeDto[];
  regexPatternDataSource: string[] = [];
  columnsToDisplay: string[] = ['regexPattern', 'actions'];
  conversationCustomPhraseTypeMode = ConversationCustomPhraseTypeMode;
  customPhraseTypeModes: GenericLookupDto[];
  invalidErrorMessage = this.localizationService.instant('Validation::RegexSyntaxInvalidPattern');
  editingForm: FormGroup;
  editIndex: number | null = null;
  customPhraseTypeDto: CustomPhraseTypeDto = null;
  maxPatternLimit = 10;

  activeTestIndex: number | null = null;
  regexTestTarget = '';
  regexTestResult: boolean | null = null;

  get addCustomPhraseTypeMessage(): string {
    if (this.customPhraseTypeList.length >= this.maxPatternLimit) {
      let exceededCustomPhraseTypeLimit = this.localizationService.instant(
        'Settings::PhraseType.ExceededCustomPhraseTypeLimit'
      );
      exceededCustomPhraseTypeLimit = exceededCustomPhraseTypeLimit.replace(
        '{0}',
        this.maxPatternLimit?.toString()
      );
      return exceededCustomPhraseTypeLimit;
    }
    return this.localizationService.instant('Settings::PhraseType.AddCustomPhraseTypes');
  }

  constructor(
    private fb: FormBuilder,
    private modalService: NgbModal,
    private toastr: ToasterService,
    private localizationService: LocalizationService,
    private phraseTypeService: PhraseTypeSettingsService,
    private store: Store,
    private confirmationService: ConfirmationService,
    private phraseTypeValidator: PhraseTypeValidator,
    private phraseTypeRegexDetailPatternValidator: PhraseTypeRegexDetailPatternValidator,
    private customPhraseTypeValidator: CustomPhraseTypeValidator
  ) {
    super();
    this.store
      .select(GenericLookupTypeState.getGenericLookups)
      .pipe(takeUntil(this.autoUnsubscribeNotifier))
      .pipe(map(filterFn => filterFn(ConversationCustomPhraseTypeMode)))
      .subscribe(result => {
        this.customPhraseTypeModes = result;
      });

    this.phraseTypeService.getLanguages(false).subscribe(res => {
      this.languagesDataSource = res;
      this.languagesDataSource.sort((x, y) => (x.name > y.name ? 1 : -1));

      if (this.languagesDataSource.length > 0) {
        this.customForm.get('languages').setValue(this.languagesDataSource);
      }
    });

    this.customForm = this.fb.group({
      id: [null],
      name: [
        null,
        {
          validators: [
            Validators.required,
            Validators.minLength(2),
            Validators.maxLength(30),
            StringValidator.whitespace,
            StringValidator.mustContainAtLeastNLetters(2),
            StringValidator.mustStartAndEndWithLetter,
            StringValidator.allowOnlyLettersDigitsAndBasicSymbols,
          ],
          asyncValidators: [this.phraseTypeValidator, this.customPhraseTypeValidator],
          updateOn: 'change',
        },
      ],
      languages: [[], [Validators.required]],
      regexDetails: [[], [Validators.required]],
      currentRegexInput: [
        null,
        {
          asyncValidators: [this.phraseTypeRegexDetailPatternValidator],
          updateOn: 'change',
        },
      ],
    });

    this.customForm
      .get('currentRegexInput')
      ?.setValidators([
        this.patternValidator.bind(this),
        this.regexDetailsRequiredValidator.bind(this),
      ]);
  }

  ngOnInit(): void {
    this.loadCustomPhraseTypes();
  }

  toggleRegexTest(index: number): void {
    if (this.activeTestIndex === index) {
      this.activeTestIndex = null;
      this.regexTestResult = null;
      this.regexTestTarget = '';
    } else {
      this.activeTestIndex = index;
      this.regexTestTarget = '';
      this.regexTestResult = null;
    }
  }

  performRegexTest(pattern: string): void {
    try {
      const regex = new RegExp(pattern);
      this.regexTestResult = regex.test(this.regexTestTarget);
    } catch {
      this.regexTestResult = false;
    }
  }

  patternValidator(control: FormGroup) {
    const value = control.value;
    if (!value || control.invalid) {
      return null;
    }

    const currentPatterns: string[] = this.customForm.get('regexDetails')?.value ?? [];
    const currentId = this.customForm.get('id')?.value;
    const existingPatterns = this.customPhraseTypeList
      .filter(pt => pt.id !== currentId)
      .reduce((acc, pt) => {
        const patterns = pt.regexDetails?.map(r => r.pattern) ?? [];
        return acc.concat(patterns);
      }, []);

    const messages: { [key: string]: any } = {};

    if (value?.startsWith(' ') || value?.endsWith(' ')) {
      messages.whiteSpaceNotAllowed = true;
    }

    if (existingPatterns.includes(value) || currentPatterns.includes(value)) {
      messages.valueAlreadyExist = true;
    }

    if (currentPatterns.length >= this.maxPatternLimit) {
      messages.maxExpressionLimitExceeded = true;
    }

    if (value.length < 4) {
      messages.minCharacterLimit = { requiredLength: 4 };
    }

    if (value.length > 200) {
      messages.maxCharacterLimit = { requiredLength: 200 };
    }

    const regex = /^[\p{L}0-9.\^\$\*\+\?\(\)\[\]\{\}\|\\\-_@!#%&:;,;'"\\`\/<=>~\s\t\n\r\f]*$/u;
    if (!regex.test(value)) {
      messages.regexMetaCharacterInvalidPattern = true;
    }

    return Object.keys(messages).length > 0 ? messages : null;
  }

  regexDetailsRequiredValidator(control: FormGroup) {
    const currentInput = control.value;
    const details = this.customForm.get('regexDetails')?.value || [];
    if (details?.length === 0 && (!currentInput || currentInput.trim() === '')) {
      return { required: true };
    }
    return null;
  }

  getSelectedIds(): number[] {
    return this.selectedCustomPhraseTypeIds;
  }

  loadCustomPhraseTypes(): void {
    this.processing = true;
    this.phraseTypeService
      .getCustomPhraseTypes()
      .pipe(take(1))
      .subscribe({
        next: response => {
          this.selectedCustomPhraseTypeIds = [];
          this.customPhraseTypeList = response;
          const enabledPhraseTypeIds = this.customPhraseTypeList
            .filter(x => x.isEnabled)
            .map(t => t.id);
          this.initiallySelectedCustomPhraseTypeIds = [...(enabledPhraseTypeIds ?? [])];
          this.selectedCustomPhraseTypeIds = [...(enabledPhraseTypeIds ?? [])];
          this.processing = false;
        },
        error: () => {
          this.processing = false;
        },
      });
  }

  isSelected(type: CustomPhraseTypeDto): boolean {
    return this.initiallySelectedCustomPhraseTypeIds.includes(type?.id);
  }

  onCustomEntityChange(event: Event, type: CustomPhraseTypeDto): void {
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) {
      this.selectedCustomPhraseTypeIds.push(type.id);
    } else {
      this.selectedCustomPhraseTypeIds = this.selectedCustomPhraseTypeIds.filter(
        id => id !== type.id
      );
    }
  }

  saveCustomPhraseType() {
    if (this.customForm.invalid || (this.editIndex != null && this.editingForm.invalid)) {
      Object.keys(this.customForm.controls).forEach(field => {
        const control = this.customForm.get(field);
        control?.markAsDirty();
        control?.updateValueAndValidity();
      });
      return;
    }

    const formValue = this.customForm.value;
    const isEditMode = formValue.id && formValue.id > 0;
    const dto: CustomPhraseTypeDto = {
      id: isEditMode ? formValue.id : 0,
      name: formValue.name,
      languages: (formValue.languages || []).join(','),
      modeId: this.conversationCustomPhraseTypeMode.regex,
      isEnabled: true,
      regexDetails: this.regexPatternDataSource.map(pattern => ({
        id: 0,
        pattern: pattern,
        customPhraseTypeId: formValue.id || 0,
      })),
      creatorFullName: null,
      lastModifierFullName: null,
      lastModificationDate: null,
    };

    this.processing = true;

    const request$ = isEditMode
      ? this.phraseTypeService.updateCustomPhraseType(dto)
      : this.phraseTypeService.createCustomPhraseTypes(dto);

    request$.subscribe({
      next: result => {
        if (isEditMode) {
          const index = this.customPhraseTypeList.findIndex(x => x.id === dto.id);
          if (index > -1) this.customPhraseTypeList[index] = result;
        } else {
          this.customPhraseTypeList.push(result);
        }
        this.customPhraseTypeList.sort((a, b) => a.name.localeCompare(b.name));
        this.processing = false;
        this.toastr.success(this.localizationService.instant('AbpUi::SuccessfullySaved'));
        this.closeModal();
      },
      error: error => {
        this.processing = false;
        this.toastr.error(error);
        this.closeModal();
      },
    });
  }

  confirmDeletion(eventArgs, dto) {
    this.confirmationService
      .warn(
        'Settings::PhraseType.CustomEntity.DeletionConfirmation',
        '::DeletionConfirmationMessage',
        {
          messageLocalizationParams: [dto.name],
          titleLocalizationParams: [dto.name],
          yesText: '::Delete',
        }
      )
      .pipe(take(1))
      .subscribe((status: Confirmation.Status) => {
        if (status === Confirmation.Status.confirm) {
          this.deleteCustomPhraseType(dto.id);
        }
      });
  }

  deleteCustomPhraseType(id: number) {
    this.phraseTypeService.deleteCustomPhraseType(id).subscribe({
      next: () => {
        const index = this.customPhraseTypeList.findIndex(x => x.id === id);
        if (index > -1) this.customPhraseTypeList.splice(index, 1);
        this.modalService.dismissAll();
        this.toastr.success(this.localizationService.instant('::SuccessfullyDeleted'));
      },
      error: err => {
        this.toastr.error(err.message, 'Error', {});
      },
    });
  }

  openModal(customPhraseType: CustomPhraseTypeDto): void {
    this.regexPatternDataSource = [];
    this.regexPatternDataSource = [...this.regexPatternDataSource];
    if (customPhraseType) {
      this.phraseTypeValidator.id = customPhraseType.id;
      this.customPhraseTypeValidator.id = customPhraseType.id;
      const patterns = (customPhraseType.regexDetails || []).map(detail => detail.pattern);
      this.customPhraseTypeDto = customPhraseType;
      this.customForm.patchValue({
        id: customPhraseType.id,
        name: customPhraseType.name,
        languages: (customPhraseType.languages || '').split(','),
        regexDetails: patterns,
      });
      this.regexPatternDataSource = patterns;
    } else {
      this.customPhraseTypeDto = null;
      this.editIndex = null;
      this.customForm.reset();
      this.editingForm?.reset();
    }

    this.modalService.open(this.customPhraseTypeModal, {
      backdrop: 'static',
      keyboard: false,
      scrollable: true,
      windowClass: 'custom-phrase-type-modal',
    });
  }

  closeModal(): void {
    this.customForm.reset();
    this.customPhraseTypeDto = null;
    this.toggleRegexTest(null);
    this.editIndex = null;
    this.editingForm?.reset();
    this.modalService.dismissAll();
  }

  removeFromSelectedLanguages(languageCode: string) {
    let selectedItems = this.customForm.get('languages').value;

    if (selectedItems) {
      const indexToRemoveFromSelected = selectedItems.findIndex(i => i === languageCode);
      if (indexToRemoveFromSelected > -1) {
        selectedItems.splice(indexToRemoveFromSelected, 1);
        this.customForm.get('languages').setValue(selectedItems);
      }
    }
  }

  addRegex() {
    const pattern = this.customForm.get('currentRegexInput')?.value?.trim();
    if (!pattern || this.customForm.get('currentRegexInput').invalid) return;

    try {
      new RegExp(pattern);
      this.regexPatternDataSource.push(pattern);
      this.regexPatternDataSource = [...this.regexPatternDataSource];
      this.syncRegexFormControl();
      this.customForm.get('currentRegexInput')?.setValue('');
    } catch {
      alert(this.invalidErrorMessage);
    }
  }

  deleteRegexPattern(index: number) {
    this.regexPatternDataSource.splice(index, 1);
    this.regexPatternDataSource = [...this.regexPatternDataSource];
    this.syncRegexFormControl();
  }

  startEditing(index: number, value: string) {
    this.editIndex = index;

    this.editingForm = this.fb.group({
      editedRegexInput: [null],
    });
    this.editingForm?.get('editedRegexInput')?.setValue(value);
    this.editingForm?.get('editedRegexInput')?.setValidators([this.patternValidator.bind(this)]);
  }

  saveEditing(index: number) {
    if (!this.editingForm?.valid) return;
    const newValue = this.editingForm?.get('editedRegexInput')?.value?.trim();

    if (newValue) {
      this.regexPatternDataSource[this.editIndex] = newValue;
      this.regexPatternDataSource = [...this.regexPatternDataSource];
      this.editIndex = null;
      this.syncRegexFormControl();
    }
  }

  cancelEditing() {
    this.editIndex = null;
    this.editingForm?.reset();
  }

  onEnterKey(event: KeyboardEvent, index: number) {
    if (event.key === 'Enter') {
      this.saveEditing(index);
    }
  }

  syncRegexFormControl(): void {
    const regexValues = this.regexPatternDataSource;
    this.customForm.get('regexDetails')?.setValue(regexValues);
    this.customForm.get('regexDetails')?.markAsDirty();
    this.customForm.get('regexDetails')?.markAsTouched();
  }

  getIsoLanguageCodes(cultureCodeString: string): string {
    if (!cultureCodeString) return '';

    const isoCodes = cultureCodeString
      .split(',')
      .map(code => code.split('-')[0].trim())
      .filter(code => !!code)
      .filter((value, index, self) => self.indexOf(value) === index)
      .sort();

    return isoCodes.join(', ');
  }
}
